import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ContactDetector } from '@workspace/data-types';
import { AuraColorType } from '../../../utils/ui-theme.utils';
import { AlertAuraComponent } from '../../layouts/alert/alert.component';

@Component({
  selector: 'aura-chat-input',
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, AlertAuraComponent],
  templateUrl: './chat-input.component.html',
  styles: [
    `
      textarea::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      textarea::-webkit-scrollbar-track {
        background: transparent;
      }
      textarea::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.15);
        border-radius: 99px;
      }
      textarea::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.3);
      }
      :host-context(.dark) textarea::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.15);
      }
      :host-context(.dark) textarea::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      textarea {
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuraChatInputComponent {
  replyText = input<string | null>(null);
  replySenderName = input<string | null>(null);

  messageSent = output<string>();
  replyCleared = output<void>();
  replyClicked = output<void>();

  form = new FormGroup({
    message: new FormControl('', {
      nonNullable: true,
      validators: [Validators.minLength(2), Validators.maxLength(4000)],
    }),
  });

  messageValue = toSignal(this.form.controls.message.valueChanges, {
    initialValue: this.form.controls.message.value,
  });

  hasConfirmedWarning = signal<boolean>(false);
  isExpanded = signal<boolean>(false);
  auraColorType = AuraColorType;

  charCount = computed(() => (this.messageValue() || '').length);
  isNearLimit = computed(() => this.charCount() >= 3800);

  textareaEl = viewChild<ElementRef<HTMLTextAreaElement>>('textareaEl');

  hasExternalContact = computed(() => {
    const val = this.messageValue();
    return ContactDetector.detect(val).hasContact;
  });

  isSendDisabled = computed(() => {
    const val = this.messageValue();
    if (!val || val.trim().length < 2) return true;
    if (this.hasExternalContact() && !this.hasConfirmedWarning()) return true;
    return false;
  });

  constructor() {
    effect(() => {
      if (!this.hasExternalContact()) {
        this.hasConfirmedWarning.set(false);
      }
    });

    effect(() => {
      // Track value changes and expansion state to adjust height dynamically
      this.messageValue();
      const expanded = this.isExpanded();
      const el = this.textareaEl()?.nativeElement;

      if (el) {
        if (expanded) {
          el.style.height = '280px';
        } else {
          // Reset style height to 'auto' to allow scrollHeight to shrink/re-calculate correctly
          el.style.height = 'auto';
          // 4 lines max: 24px (padding p-3) + 4 * 20px (line-height) = 104px
          // 1 line min: 24px + 20px = 44px
          const newHeight = Math.min(104, Math.max(44, el.scrollHeight));
          el.style.height = `${newHeight}px`;
        }
      }
    });
  }

  isTransitioning = signal<boolean>(false);

  toggleExpand(): void {
    this.isTransitioning.set(true);
    this.isExpanded.update((prev) => !prev);
    setTimeout(() => {
      this.isTransitioning.set(false);
    }, 300);
  }

  send(): void {
    if (this.isSendDisabled()) {
      return;
    }
    const val = this.form.controls.message.value;
    if (val && val.trim()) {
      this.messageSent.emit(val);
      this.form.reset();
      this.hasConfirmedWarning.set(false);
    }
  }

  clearReply(): void {
    this.replyCleared.emit();
  }

  confirmSendAnyway(): void {
    this.hasConfirmedWarning.set(true);
  }
}
